import React, { lazy, Suspense, useEffect, useState, useRef } from "react";
import { auth, db } from "./firebase/config";
import {
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import AddProduct from "./components/Auth/AddProduct";
import JobFeed from "./components/Dashboard/JobFeed";
import Home from "./components/Dashboard/Home";
import MyFarm from "./components/Dashboard/MyFarm";
import FieldListingForm from "./components/Dashboard/FieldListingForm";
import Inbox from "./components/Messaging/Inbox";
import Profile from "./components/Auth/Profile";
import { Link, Navigate, Routes, Route, useNavigate, useLocation  } from "react-router-dom";
import Navbar from "./components/Navbar";
import EditProfile from "./components/Auth/EditProfile";
import ProtectedRoute from "./components/ProtectedRoute";
import ChatWrapper from "./components/Messaging/ChatWrapper";
import BrowseUsers from "./components/Dashboard/BrowseUsers";
import "./index.css";
import AddEvent from "./components/Events/AddEvent";
import AuthLanding from "./components/Auth/AuthLanding";
import ReactivateAccount from "./components/Auth/ReactivateAccount";
import AddFarmStand from "./components/Auth/AddFarmStand";
import Terms from "./components/Legal/Terms";
import Privacy from "./components/Legal/Privacy";
import CommunityRules from "./components/Legal/CommunityRules";
import Safety from "./components/Legal/Safety";
import Contact from "./components/Legal/Contact";
import CampaignTrafficTracker, { trackUsageEvent } from "./components/Analytics/CampaignTrafficTracker";
import SubscribePage from "./components/Billing/SubscribePage";
import CommunityFeed from "./components/Community/CommunityFeed";
import CommunityAlertsPage from "./components/Community/CommunityAlertsPage";
import AlmanacPage from "./components/Almanac/AlmanacPage";
import LandSharePage from "./components/LandShare/LandSharePage";
import PublicFarmProfile from "./components/PublicFarmProfile/PublicFarmProfile";
import PublicFarmStands from "./components/Dashboard/PublicFarmStands";
import PublicHome from "./components/Dashboard/PublicHome";
import PublicHomepageSwitch from "./components/LocalLive/PublicHomepageSwitch";
import { AuthActionGateProvider } from "./components/Common/AuthActionGate";
import { MARKETPLACE_SECTIONS } from "./constants/plantsGarden";
import { geocodeLocation, reverseGeocode } from "./utils/geocode";
import {
  describeGeolocationError,
  getBrowserGeolocation,
  getGeolocationPermissionState,
  getGeolocationSupportStatus,
} from "./utils/browserLocation";
import {
  LOCATION_AREA_ERROR,
  looksLikeStreetAddress,
  normalizeBroadLocation,
  validateBroadLocationInput,
} from "./utils/locationArea";
import {
  DEFAULT_FALLBACK_LOCATION,
  LOCATION_SOURCES,
  buildPrivateProfileLocationFields,
  buildPublicProfileLocationFields,
  getLocationDiagnostics,
  getSafeDistanceMiles,
  hasValidCoordinates,
  isDefaultFallbackLocation,
  normalizeLocationRecord,
  normalizeProfileLocation,
} from "./utils/locationUtils";
import {
  canPerformTrustedAction,
  shouldShowVerificationWarning,
} from "./utils/emailVerification";
import {
  getVerificationErrorMessage,
  sendVerificationEmail,
} from "./utils/verificationEmail";
import {
  isLocalLiveParticipantPath,
  shouldSuppressNormalShellControls,
  shouldRenderEmailVerificationOnboarding,
  shouldRenderLocationSetupOnboarding,
  shouldRenderNormalAppLoading,
} from "./utils/appRouteOnboarding";
import { AppPageAnalyticsTracker, trackAppEvent } from "./utils/appAnalytics";

const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const ContentStudio = lazy(() => import("./components/Admin/ContentStudio"));
const FarmRecordsPage = lazy(() => import("./components/FarmRecords/FarmRecordsPage"));
const GoatGuide = lazy(() => import("./components/Onboarding/GoatGuide"));
const StandShelf = lazy(() => import("./components/Dashboard/StandShelf"));
const BillyAnimationTest = lazy(() => import("./components/Onboarding/BillyAnimationTest"));
const PublicLocalLive = lazy(() => import("./components/LocalLive/PublicLocalLive"));
const PublicFallCrawl2026 = lazy(() => import("./components/LocalLive/PublicFallCrawl2026"));
const LocalLiveParticipant = lazy(() => import("./components/LocalLive/LocalLiveParticipant"));
const AdminLocalLive = lazy(() => import("./components/LocalLive/AdminLocalLive"));
const FallCrawlHomepagePreview = lazy(() => import("./components/LocalLive/FallCrawlHomepagePreview"));
const LocalLiveBackroadsBookTest = lazy(() => import("./components/LocalLive/LocalLiveBackroadsBookTest"));
const LocalLiveMapPreview = lazy(() => import("./components/LocalLive/LocalLiveMapPreview"));
const FarmHelpCenterPage = lazy(() => import("./components/FarmHelp/FarmHelpCenterPage"));
const EventRegistrationPage = lazy(() => import("./components/Events/EventRegistrationPage"));

const NotFoundPage = () => (
  <main className="page-shell page-shell-narrow">
    <section className="card">
      <h1>Page not found</h1>
      <p>That page is not available. Head back home to keep browsing MyFarmingPal.</p>
      <a className="btn-primary" href="/">
        Go Home
      </a>
    </section>
  </main>
);

const RouteLoading = ({ message = "Loading..." }) => (
  <main className="page-shell page-shell-narrow">
    <section className="card">
      <p>{message}</p>
    </section>
  </main>
);

const getAdminGateStatus = ({ user, userProfile, authLoading, profileLoading }) => {
  if (authLoading || profileLoading) return "loading";
  if (!user?.uid) return "signed-out";
  if (userProfile?.accountStatus === "banned") return "banned";
  if (userProfile?.accountStatus === "deactivated") return "deactivated";
  if (userProfile?.admin === true || userProfile?.isAdmin === true) return "admin";
  return "unauthorized";
};

const AdminRoutePage = ({ user, userProfile, authLoading, profileLoading }) => {
  const gateStatus = getAdminGateStatus({
    user,
    userProfile,
    authLoading,
    profileLoading,
  });

  if (gateStatus === "admin") {
    return (
      <Suspense fallback={<RouteLoading message="Loading admin dashboard..." />}>
        <AdminDashboard />
      </Suspense>
    );
  }

  return (
    <main className="admin-access-page" aria-label="Admin Dashboard">
      <section className="section-header admin-header">
        <div>
          <p className="eyebrow">Launch Control</p>
          <h1>Admin Dashboard</h1>
          <p className="muted">
            Private tools for managing MyFarmingPal users, listings, reports,
            founder launch, and security.
          </p>
        </div>
        <Link className="admin-action-link" to="/login">
          Sign In
        </Link>
      </section>

      {gateStatus === "loading" && (
        <div className="admin-status-message">
          <strong>Loading admin access...</strong>
          <span>Checking your account and admin profile before loading tools.</span>
        </div>
      )}

      {gateStatus === "signed-out" && (
        <div className="admin-status-message admin-status-warning">
          <strong>Please sign in to view admin tools.</strong>
          <span>Admin Dashboard is private and requires an admin account.</span>
        </div>
      )}

      {gateStatus === "unauthorized" && (
        <div className="admin-status-message admin-status-warning">
          <strong>Admin access only</strong>
          <span>You do not have permission to view this page.</span>
        </div>
      )}

      {gateStatus === "banned" && (
        <div className="admin-status-message admin-status-warning">
          <strong>Account restricted</strong>
          <span>This account has been restricted and cannot access MyFarmingPal.</span>
        </div>
      )}

      {gateStatus === "deactivated" && (
        <div className="admin-status-message admin-status-warning">
          <strong>Account deactivated</strong>
          <span>Please reactivate your account before opening admin tools.</span>
        </div>
      )}
    </main>
  );
};

const SOUND_MAP = {
  default: null,
  goat: "/sounds/goat.mp3",
  cow: "/sounds/cow.wav",
  pig: "/sounds/pig.mp3",
  rooster: "/sounds/rooster.mp3",
  horse: "/sounds/horse.mp3",
  chicken: "/sounds/chicken.mp3",
  duck: "/sounds/duck.mp3",
  donkey: "/sounds/donkey.mp3",
};

const ALERT_SAFETY_BUFFER_MS = 10000;
const MAX_ALERTED_NOTIFICATION_IDS = 200;

const DEFAULT_LOCATION = DEFAULT_FALLBACK_LOCATION;

const getAlertedNotificationStorageKey = (uid) =>
  uid ? `myfarmingpal_alerted_notifications_${uid}` : "";

const readAlertedNotificationIds = (uid) => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(getAlertedNotificationStorageKey(uid));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    console.warn("Failed reading alerted notification ids:", error);
    return [];
  }
};

const hasAlertedNotificationId = (uid, id) => {
  if (!uid || !id) return false;
  return readAlertedNotificationIds(uid).includes(id);
};

const rememberAlertedNotificationId = (uid, id) => {
  if (typeof window === "undefined" || !uid || !id) return;

  try {
    const existing = readAlertedNotificationIds(uid).filter((item) => item !== id);
    const next = [id, ...existing].slice(0, MAX_ALERTED_NOTIFICATION_IDS);
    window.localStorage.setItem(
      getAlertedNotificationStorageKey(uid),
      JSON.stringify(next)
    );
  } catch (error) {
    console.warn("Failed storing alerted notification id:", error);
  }
};

const getNotificationTimeMs = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (typeof value?.seconds === "number") return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "number") return value > 100000000000 ? value : value * 1000;
  }

  return 0;
};

const isSuppressedNotificationRecord = (record = {}) =>
  record.read === true ||
  record.seen === true ||
  record.dismissed === true ||
  record.archived === true ||
  record.isArchived === true ||
  record.status === "archived" ||
  record.status === "dismissed";

const hasCoordinates = (locationData) =>
  hasValidCoordinates(locationData);

const isDefaultNashvilleLocation = isDefaultFallbackLocation;

const getSavedLocationFromProfile = (profileData = {}) => {
  const savedLocation = normalizeProfileLocation(profileData);
  if (!savedLocation) return null;
  if (looksLikeStreetAddress(savedLocation.locationText)) return null;
  if (
    isDefaultNashvilleLocation(savedLocation) &&
    !["manual-entry", "skipped-final-fallback"].includes(savedLocation.locationSource)
  ) {
    return null;
  }

  return savedLocation;
};

const getPublicLocationUpdatePayload = (locationData = {}) => {
  const publicFields = buildPublicProfileLocationFields(locationData);

  return {
    ...publicFields,
    updatedAt: serverTimestamp(),
    locationUpdatedAt: serverTimestamp(),
    location: {
      ...publicFields,
      updatedAt: serverTimestamp(),
    },
  };
};

const getPrivateLocationUpdatePayload = (locationData = {}) => ({
  ...buildPrivateProfileLocationFields(locationData),
  updatedAt: serverTimestamp(),
});

const getPrivateLocationRef = (uid) => {
  if (!uid) return null;
  return doc(db, "users", uid, "private", "location");
};

const getPrivateSavedLocation = async (uid, publicProfile = {}) => {
  const privateLocationRef = getPrivateLocationRef(uid);
  if (!privateLocationRef) return null;

  try {
    const snap = await getDoc(privateLocationRef);
    if (!snap.exists()) return null;

    const privateLocation = snap.data();
    return normalizeLocationRecord(
      {
        ...privateLocation,
        city: publicProfile.city || privateLocation.city,
        county: publicProfile.county || privateLocation.county,
        state: publicProfile.state || privateLocation.state,
        locationText: publicProfile.locationText || privateLocation.locationText,
        locationSource:
          privateLocation.locationSource ||
          publicProfile.locationSource ||
          LOCATION_SOURCES.SAVED_PROFILE,
      },
      "",
      LOCATION_SOURCES.SAVED_PROFILE
    );
  } catch (error) {
    console.warn("Failed reading private saved location:", error);
    return null;
  }
};

const logLocationSource = (source, locationData) => {
  console.log("\uD83D\uDCCD Location source:", source, locationData);
};

const getReadableLocationLabel = (locationData = {}) =>
  locationData.locationText ||
  [locationData.city || locationData.county, locationData.state]
    .filter(Boolean)
    .join(", ") ||
  "your saved area";

const resolvePositionLocation = async (position) => {
  const baseLocation = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
  };

  try {
    const reverseLocation = await reverseGeocode(baseLocation.lat, baseLocation.lng);
    return {
      ...baseLocation,
      ...(reverseLocation || {}),
    };
  } catch (error) {
    console.warn("Reverse geocode unavailable after GPS success:", error);
    return baseLocation;
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [manualLocationInput, setManualLocationInput] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [locationPromptMessage, setLocationPromptMessage] = useState("");
  const [locationPromptMessageType, setLocationPromptMessageType] = useState("info");
  const [verificationRefreshKey, setVerificationRefreshKey] = useState(0);
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationStatusMessage, setVerificationStatusMessage] = useState("");
  const navigate = useNavigate();

  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

  const isAuthPage =
  normalizedPath === "/login" ||
  normalizedPath === "/register";
  const publicPagePaths = new Set([
    "/",
    "/login",
    "/register",
    "/terms",
    "/privacy",
    "/community-rules",
    "/safety",
    "/contact",
    "/home",
    "/marketplace",
    "/farm-stands",
    "/events",
    "/jobs",
    "/plants-garden",
    "/land-share",
    "/almanac",
  ]);
  const isPublicFarmPage =
    normalizedPath === "/farm" || normalizedPath.startsWith("/farm/");
  const isPublicStandPage =
    normalizedPath === "/stand" || normalizedPath.startsWith("/stand/");
  const isPublicLocalLivePage = normalizedPath.startsWith("/local-live/");
  const isPublicEventRegistrationPage = normalizedPath.startsWith("/event-registration/") ||
    normalizedPath === "/christmas-crawl/2026/register";
  const isLocalLiveParticipantPage = isLocalLiveParticipantPath(normalizedPath);
  const isFallCrawlPage = normalizedPath.startsWith("/fall-crawl/");
  const isFallCrawlAdminPreviewPage =
    /^\/admin\/local-live\/[^/]+\/(?:homepage-preview|map-preview|backroads-book-test)$/.test(normalizedPath);
  const isAdminRoute = normalizedPath === "/admin";
  const shouldSuppressOuterAppChrome = isFallCrawlPage || isFallCrawlAdminPreviewPage;
  const shouldHideNormalShellControls = shouldSuppressNormalShellControls({
    isLocalLiveParticipantPage,
    shouldSuppressOuterAppChrome,
  });
  const isPublicPage = publicPagePaths.has(normalizedPath) || isPublicFarmPage || isPublicStandPage || isPublicLocalLivePage || isFallCrawlPage || isPublicEventRegistrationPage;
  const shouldShowBillyGuide =
    Boolean(user?.uid) &&
    !authLoading &&
    !shouldSuppressOuterAppChrome &&
    !isPublicPage &&
    location.pathname !== "/billy-animation-test";
  const isLocalhostRuntime =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const locationDebugEnabled =
    isLocalhostRuntime &&
    new URLSearchParams(location.search || "").get("locationDebug") === "1";

  const lastNotifiedMap = useRef({});
  const seenListingIdsRef = useRef(new Set());
  const seenProductIdsRef = useRef(new Set());
  const seenBidIdsRef = useRef(new Set());
  const seenAcceptedBidIdsRef = useRef(new Set());
  const loggedInAnalyticsRef = useRef("");

  const notificationSoundRef = useRef(null);
  const lastSoundPlayRef = useRef(0);
  const goatSoundUnlockedRef = useRef(false);
  const lastGoatPlayRef = useRef(0);

  const handleLogout = async () => {
  try {
    // Clear app-side state first so listener effects begin shutting down.
    setNotifPrefs(null);
    setUserProfile(null);
    setUser(null);

    // Move away from protected/admin/listener-heavy pages.
    navigate("/", { replace: true });

    // Give React time to unmount protected routes and listener effects.
    await new Promise((resolve) => setTimeout(resolve, 200));

    await signOut(auth);
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

  const saveUserLocation = async (locationData) => {
      if (!user?.uid) return;

      try {
      const userRef = doc(db, "users", user.uid);
      const batch = writeBatch(db);

      batch.update(userRef, getPublicLocationUpdatePayload(locationData));

      if (hasValidCoordinates(locationData)) {
        batch.set(
          getPrivateLocationRef(user.uid),
          getPrivateLocationUpdatePayload(locationData),
          { merge: true }
        );
      }

      await batch.commit();
    } catch (error) {
      console.error("Failed saving user location:", error);
      throw error;
    }
  };

  const showLocationPromptMessage = (message, type = "info") => {
    setLocationPromptMessage(message);
    setLocationPromptMessageType(type);
  };

  const handleUseCurrentLocation = async () => {
    showLocationPromptMessage("");

    const support = getGeolocationSupportStatus();
    if (!support.ok) {
      const details = describeGeolocationError(support);
      const helpText = Array.isArray(details.helpText)
        ? details.helpText.join(" ")
        : details.helpText;
      showLocationPromptMessage(
        [details.message, helpText].filter(Boolean).join(" "),
        "error"
      );
      return;
    }

    try {
      setIsLocating(true);

      const permissionState = await getGeolocationPermissionState();
      if (permissionState === "denied") {
        throw { code: "permission_denied" };
      }

      const position = await getBrowserGeolocation();
      const locationData = normalizeLocationRecord(await resolvePositionLocation(position));
      locationData.locationSource = LOCATION_SOURCES.BROWSER_GPS;

      console.log("GPS success");
      logLocationSource("gps", locationData);

      try {
        await saveUserLocation(locationData);
      } catch (saveError) {
        console.error("GPS location resolved but could not be saved:", saveError);
        showLocationPromptMessage(
          "We found your general area, but could not save it right now. Please try again, or enter your city/county manually.",
          "error"
        );
        return;
      }

      setUserLocation(locationData);
      showLocationPromptMessage(
        `Saved your general location as ${getReadableLocationLabel(locationData)}.`,
        "success"
      );
      window.setTimeout(() => setShowLocationPrompt(false), 1200);
    } catch (error) {
      const details = describeGeolocationError(error);
      console.warn("GPS location failed", {
        errorCode: details.code,
        errorMessage: error?.message || "",
      });
      const helpText = Array.isArray(details.helpText)
        ? details.helpText.join(" ")
        : details.helpText;
      showLocationPromptMessage(
        [details.message, helpText].filter(Boolean).join(" "),
        "error"
      );
    } finally {
      setIsLocating(false);
    }
  };

  const handleSaveManualLocation = async (event) => {
    event.preventDefault();

    const cleanLocation = manualLocationInput.trim();
    if (!cleanLocation) {
      showLocationPromptMessage("Please enter a county/state or city/state area.", "error");
      return;
    }

    const validation = validateBroadLocationInput(cleanLocation);
    if (!validation.ok) {
      showLocationPromptMessage(validation.error || LOCATION_AREA_ERROR, "error");
      return;
    }

    try {
      setIsSavingLocation(true);
      showLocationPromptMessage("");

      let locationData = normalizeLocationRecord({
        ...normalizeBroadLocation({ locationText: cleanLocation }, cleanLocation),
        locationSource: LOCATION_SOURCES.EXPLICIT,
      });

      const geocoded = await geocodeLocation(cleanLocation);
      if (geocoded && typeof geocoded.lat === "number" && typeof geocoded.lng === "number") {
        const reverseLocation = await reverseGeocode(geocoded.lat, geocoded.lng);
        locationData = normalizeLocationRecord({
          lat: geocoded.lat,
          lng: geocoded.lng,
          ...normalizeBroadLocation(reverseLocation || {}, cleanLocation),
          locationSource: LOCATION_SOURCES.EXPLICIT,
        });

        logLocationSource("manual-entry", locationData);
      }

      if (!hasCoordinates(locationData)) {
        logLocationSource("manual-entry-geocode-missing", locationData);
        showLocationPromptMessage(
          "Area saved without map coordinates. You can correct it later if nearby results look off.",
          "warning"
        );
      }

      await saveUserLocation(locationData);
      setUserLocation(locationData);
      if (hasCoordinates(locationData)) {
        showLocationPromptMessage(
          `Saved your general location as ${getReadableLocationLabel(locationData)}.`,
          "success"
        );
      }
      window.setTimeout(() => setShowLocationPrompt(false), 1200);
    } catch (error) {
      console.error("Failed saving manual location:", error);
      showLocationPromptMessage(
        "We could not save that location right now. Please try again, or contact support if it keeps happening.",
        "error"
      );
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleSkipLocationSetup = () => {
    const fallbackLocation = {
      ...DEFAULT_LOCATION,
      locationSource: LOCATION_SOURCES.FALLBACK,
    };

    console.log("Using final fallback location");
    logLocationSource("skipped-final-fallback", fallbackLocation);
    setUserLocation(fallbackLocation);
    setShowLocationPrompt(false);
  };

  const playNotificationSound = async () => {
  try {
    const selectedSound = notifPrefs?.sound || "default";
    const soundPath = SOUND_MAP[selectedSound];

    if (!soundPath) return;
    if (!notificationSoundRef.current) return;

    const now = Date.now();

    // prevent duplicate rapid plays
    if (now - lastSoundPlayRef.current < 900) return;

    notificationSoundRef.current.pause();
    notificationSoundRef.current.currentTime = 0;

    await notificationSoundRef.current.play();
    lastSoundPlayRef.current = now;
  } catch (error) {
    console.log("Custom notification sound failed:", error);
  }
};

const handleSnapshotError = (label) => (error) => {
  // Expected during logout / account status changes as listeners shut down.
  if (error.code === "permission-denied") {
    console.warn(`${label} listener closed during auth change.`);
    return;
  }

  console.error(`${label} listener failed:`, error.code, error.message);
};

  const handleResendVerificationEmail = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      alert("Please log in first.");
      return;
    }

    try {
      setVerificationSending(true);
      await sendVerificationEmail(currentUser);
      setVerificationStatusMessage("Verification email sent. Please check your inbox.");
    } catch (error) {
      console.error("Failed sending verification email:", {
        code: error?.code,
        message: error?.message,
      });
      setVerificationStatusMessage(getVerificationErrorMessage(error));
    } finally {
      setVerificationSending(false);
    }
  };

  const handleRefreshVerifiedAccount = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      alert("Please log in first.");
      return;
    }

    try {
      await currentUser.reload();
      setUser(auth.currentUser);
      setVerificationRefreshKey((value) => value + 1);

      if (canPerformTrustedAction(auth.currentUser, userProfile)) {
        setVerificationStatusMessage("Thanks! Your email is verified.");
      } else {
        setVerificationStatusMessage("Your email is not verified yet. Please check your inbox and try again.");
      }
    } catch (error) {
      console.error("Failed refreshing account:", error);
      setVerificationStatusMessage("Could not refresh your account: " + error.message);
    }
  };

  // 🐐 Load goat sound once + unlock it after first user interaction
    useEffect(() => {
  const selectedSound = notifPrefs?.sound || "default";
  const soundPath = SOUND_MAP[selectedSound];

  if (!soundPath) {
    if (notificationSoundRef.current) {
      notificationSoundRef.current.pause();
      notificationSoundRef.current = null;
    }
    return;
  }

  const audio = new Audio(soundPath);
  audio.preload = "auto";
  audio.volume = 0.6;

  notificationSoundRef.current = audio;
}, [notifPrefs?.sound]);

  // 🔐 Auth listener

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);

      if (u?.uid && loggedInAnalyticsRef.current !== u.uid) {
        loggedInAnalyticsRef.current = u.uid;
        trackAppEvent("login_completed", {
          feature: "auth",
          action: "auth_state_ready",
        });
      }

      if (!u?.uid) {
        loggedInAnalyticsRef.current = "";
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
  if (!user) {
    setUserProfile(null);
    setProfileLoading(false);
    return;
  }

  setProfileLoading(true);

  const unsub = onSnapshot(
    doc(db, "users", user.uid),
    (snap) => {
      if (snap.exists()) {
        setUserProfile({ id: snap.id, ...snap.data() });
      } else {
        setUserProfile(null);
      }

      setProfileLoading(false);
    },
    (error) => {
  if (error.code !== "permission-denied") {
    console.error("Failed loading user profile:", error);
  }

  setUserProfile(null);
  setProfileLoading(false);
}
  );

  return () => unsub();
}, [user]);

  // 📍 Location handling
  useEffect(() => {
    if (authLoading) return;

    if (!user?.uid) {
      setUserLocation(null);
      setShowLocationPrompt(false);
      showLocationPromptMessage("");
      return;
    }

    const fallbackToDefault = () => {
      console.log("No saved location, asking user to choose location setup");
      showLocationPromptMessage(
        "Use your current location, or enter a city/county manually.",
        "info"
      );
      setShowLocationPrompt(true);
    };

    const loadSavedLocation = async () => {
      if (!user) return false;

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const data = snap.data();
          const privateSavedLocation = await getPrivateSavedLocation(user.uid, data);
          if (privateSavedLocation && hasCoordinates(privateSavedLocation)) {
            logLocationSource("saved-private-location", privateSavedLocation);
            setUserLocation(privateSavedLocation);
            return true;
          }

          const savedLocation = getSavedLocationFromProfile(data);

          if (savedLocation) {
            console.log("Using saved location");

            if (hasCoordinates(savedLocation)) {
              logLocationSource(
                savedLocation.locationSource === LOCATION_SOURCES.FALLBACK ||
                  savedLocation.locationSource === "skipped-final-fallback"
                  ? LOCATION_SOURCES.FALLBACK
                  : "saved-profile-latlng",
                savedLocation
              );
              setUserLocation(savedLocation);
              return true;
            }

            if (savedLocation.locationText) {
              const geocoded = await geocodeLocation(savedLocation.locationText);
              if (
                geocoded &&
                typeof geocoded.lat === "number" &&
                typeof geocoded.lng === "number"
              ) {
                const reverseLocation = await reverseGeocode(geocoded.lat, geocoded.lng);
                const resolvedLocation = normalizeLocationRecord({
                  ...savedLocation,
                  lat: geocoded.lat,
                  lng: geocoded.lng,
                  city: reverseLocation?.city || savedLocation.city,
                  state: reverseLocation?.state || savedLocation.state,
                  county: reverseLocation?.county || savedLocation.county,
                  locationText:
                    reverseLocation?.locationText ||
                    geocoded.displayName ||
                    savedLocation.locationText,
                });

                resolvedLocation.locationSource = LOCATION_SOURCES.SAVED_PROFILE;
                logLocationSource("saved-profile-text-geocoded", resolvedLocation);
                await saveUserLocation(resolvedLocation);
                setUserLocation(resolvedLocation);

                return true;
              }

              setManualLocationInput(savedLocation.locationText);
              return false;
            }
          }
          if (hasCoordinates(data) && !isDefaultNashvilleLocation(data)) {
            const fallbackSavedLocation = normalizeLocationRecord(data);
            fallbackSavedLocation.locationSource = LOCATION_SOURCES.SAVED_PROFILE;
            logLocationSource("saved-profile-latlng", fallbackSavedLocation);
            setUserLocation(fallbackSavedLocation);
            return true;
          }
        }
      } catch (error) {
        console.error("Failed loading saved location:", error);
      }

      return false;
    };

    loadSavedLocation().then((found) => {
      if (found) {
        setShowLocationPrompt(false);
        showLocationPromptMessage("");
        return;
      }

      fallbackToDefault();
    });

    return undefined;
  }, [authLoading, user?.uid]);

  // 🟢 Online heartbeat
useEffect(() => {
  if (!user?.uid) return;

  // Do not keep pinging lastActive for banned/deactivated accounts.
  if (userProfile?.accountStatus && userProfile.accountStatus !== "active") {
    return;
  }

  let cancelled = false;

  const updateStatus = async () => {
    const liveUser = auth.currentUser;

    // Prevent logout timing errors.
    if (cancelled) return;
    if (!liveUser?.uid) return;
    if (liveUser.uid !== user.uid) return;

    try {
      await updateDoc(doc(db, "users", liveUser.uid), {
        lastActive: serverTimestamp(),
      });
    } catch (error) {
      // This can happen briefly during logout or account deactivation.
      if (error.code !== "permission-denied") {
        console.error("Failed updating lastActive:", error);
      }
    }
  };

  updateStatus();

  // 90 seconds is plenty for online dots and saves Firestore writes.
  const intervalId = setInterval(updateStatus, 90000);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
}, [user?.uid, userProfile?.accountStatus]);

  // 🔔 Request notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // 🔔 Load notification preferences
  useEffect(() => {
    if (!user) {
      setNotifPrefs(null);
      return;
    }

    const loadPrefs = async () => {
      try {
        console.log("🔎 Loading notif prefs user doc:", user.uid);
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          setNotifPrefs(
            snap.data().notifPrefs || {
              chat: true,
              listings: true,
              products: true,
              bids: true,
            }
          );
        }
      } catch (error) {
  console.error("❌ Failed loading notification prefs for user:", user.uid, error);
}
    };

    loadPrefs();
  }, [user]);

  const canNotify = () =>
    "Notification" in window && Notification.permission === "granted";

  const shouldTriggerLiveAlert = ({
    id,
    record = {},
    listenerStartedAt,
    createdAt,
    updatedAt,
  }) => {
    if (!user?.uid || !id) return false;
    if (isSuppressedNotificationRecord(record)) return false;
    if (hasAlertedNotificationId(user.uid, id)) return false;

    const alertTime = getNotificationTimeMs(
      createdAt,
      record.createdAt,
      updatedAt,
      record.updatedAt,
      record.lastMessageAt
    );

    if (!alertTime) return false;
    if (listenerStartedAt && alertTime < listenerStartedAt - ALERT_SAFETY_BUFFER_MS) {
      return false;
    }

    rememberAlertedNotificationId(user.uid, id);
    return true;
  };

  // 📏 Distance helper
  useEffect(() => {
  if (!user || userProfile?.accountStatus !== "active" || !notifPrefs?.events) return;
  if (!canNotify()) return;

  const seenReservationIds = new Set();

  let hostInitialized = false;
  let vendorInitialized = false;
  const listenerStartedAt = Date.now();

  const hostReservationsQuery = query(
    collection(db, "eventReservations"),
    where("hostId", "==", user.uid)
  );

  const vendorReservationsQuery = query(
    collection(db, "eventReservations"),
    where("userId", "==", user.uid)
  );

  const handleReservationSnapshot = async (snapshot, type) => {
    // 🚫 skip initial load, prevents notification spam on refresh
    if (type === "host" && !hostInitialized) {
      snapshot.docs.forEach((docSnap) => {
        seenReservationIds.add(docSnap.id);
      });
      hostInitialized = true;
      return;
    }

    if (type === "vendor" && !vendorInitialized) {
      snapshot.docs.forEach((docSnap) => {
        seenReservationIds.add(docSnap.id);
      });
      vendorInitialized = true;
      return;
    }

    for (const change of snapshot.docChanges()) {
      if (change.type !== "added" && change.type !== "modified") continue;

      const id = change.doc.id;
      const data = change.doc.data();

      if (seenReservationIds.has(id)) continue;
      seenReservationIds.add(id);

      // 🔑 YOU ARE THE HOST: new reservation request
      if (type === "host" && data.status === "pending") {
        if (
          !shouldTriggerLiveAlert({
            id: `reservation_${type}_${id}_${data.status}`,
            record: data,
            listenerStartedAt,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          })
        ) {
          continue;
        }

        playNotificationSound();

        const notification = new Notification("📩 New Reservation Request", {
          body: `${data.userName || "Someone"} requested a booth`,
          icon: "/logo192.png",
          tag: `reservation_${id}_${data.status}`,
        });

        notification.onclick = () => {
          window.focus();
          navigate("/home", { state: { openEventsTab: true } });
          notification.close();
        };
      }

      // 🔑 YOU ARE THE VENDOR: accepted / paid
      if (type === "vendor") {
        if (data.status === "accepted") {
          if (
            !shouldTriggerLiveAlert({
              id: `reservation_${type}_${id}_${data.status}`,
              record: data,
              listenerStartedAt,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            })
          ) {
            continue;
          }

          playNotificationSound();

          const notification = new Notification("✅ Reservation Accepted", {
            body: `Your spot for ${data.eventTitle || "event"} is confirmed`,
            icon: "/logo192.png",
            tag: `reservation_${id}_${data.status}`,
          });

          notification.onclick = () => {
            window.focus();
            navigate("/home", { state: { openEventsTab: true } });
            notification.close();
          };
        }

        if (data.status === "paid") {
          if (
            !shouldTriggerLiveAlert({
              id: `reservation_${type}_${id}_${data.status}`,
              record: data,
              listenerStartedAt,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            })
          ) {
            continue;
          }

          playNotificationSound();

          const notification = new Notification("💵 Payment Confirmed", {
            body: `You're fully booked for ${data.eventTitle || "event"}`,
            icon: "/logo192.png",
            tag: `reservation_${id}_${data.status}`,
          });

          notification.onclick = () => {
            window.focus();
            navigate("/home", { state: { openEventsTab: true } });
            notification.close();
          };
        }
      }
    }
  };

  const unsubHostReservations = onSnapshot(
    hostReservationsQuery,
    (snapshot) => handleReservationSnapshot(snapshot, "host"),
    handleSnapshotError("Host reservation")
  );

  const unsubVendorReservations = onSnapshot(
    vendorReservationsQuery,
    (snapshot) => handleReservationSnapshot(snapshot, "vendor"),
    handleSnapshotError("Vendor reservation")
  );

  return () => {
    unsubHostReservations();
    unsubVendorReservations();
  };
}, [user, userProfile?.accountStatus, notifPrefs, navigate]);

  // 💬 Chat message notifications
  useEffect(() => {
    if (!user || userProfile?.accountStatus !== "active" || !notifPrefs?.chat || !canNotify()) return;

    const convoQuery = query(
      collection(db, "conversations"),
      where("users", "array-contains", user.uid)
    );
    let hasInitializedChatNotifications = false;
    const listenerStartedAt = Date.now();

    const unsub = onSnapshot(convoQuery, async (snapshot) => {
      if (!hasInitializedChatNotifications) {
        snapshot.docs.forEach((docSnap) => {
          const convo = docSnap.data();
          const lastMessageAt = getNotificationTimeMs(convo.lastMessageAt);
          if (lastMessageAt) {
            lastNotifiedMap.current[docSnap.id] = lastMessageAt;
          }
        });
        hasInitializedChatNotifications = true;
        return;
      }

      for (const change of snapshot.docChanges()) {
        if (change.type !== "modified") continue;

        const conversationId = change.doc.id;
        const convo = change.doc.data();

        const lastSenderId = convo.lastSenderId;
        const unreadCount = convo.unreadCount?.[user.uid] || 0;
        const lastMessage = convo.lastMessage || "You received a new message";
        const lastMessageAt = getNotificationTimeMs(convo.lastMessageAt);
        const messageData = {
          messageType: convo.lastMessageType,
          official: convo.lastMessageOfficial,
          senderRole: convo.lastSenderRole,
        };
        const conversationData = convo;
        const isOfficialAdminConversation =
          conversationData?.isAdminConversation === true ||
          conversationData?.adminMessageThread === true ||
          conversationData?.official === true;

        if (!lastSenderId || lastSenderId === user.uid) continue;
        if (!lastMessageAt) continue;
        if (unreadCount <= 0) continue;

        const lastSeenTimestamp =
          lastNotifiedMap.current[conversationId] || 0;

        if (lastMessageAt <= lastSeenTimestamp) continue;
        if (
          !shouldTriggerLiveAlert({
            id: `chat_${conversationId}_${lastMessageAt}`,
            record: convo,
            listenerStartedAt,
            createdAt: convo.lastMessageAt,
            updatedAt: convo.updatedAt,
          })
        ) {
          lastNotifiedMap.current[conversationId] = lastMessageAt;
          continue;
        }

        // do not show popup if already inside that exact chat
        if (window.location.pathname === `/chat/${conversationId}`) {
          lastNotifiedMap.current[conversationId] = lastMessageAt;
          continue;
        }

        lastNotifiedMap.current[conversationId] = lastMessageAt;

        let senderName = "Someone";
        try {
          const senderSnap = await getDoc(doc(db, "users", lastSenderId));
          if (senderSnap.exists()) {
            senderName = senderSnap.data().name || senderName;
          }
        } catch (error) {
          console.error("Failed to fetch sender for chat notification:", error);
        }

        const notificationTitle = isOfficialAdminConversation
          ? "Message from MyFarmingPal Admin"
          : `💬 New message from ${senderName}`;

        try {
          playNotificationSound();

          const notification = new Notification(
            notificationTitle,
            {
              body: lastMessage,
              icon: "/logo192.png",
              tag: `chat_${conversationId}`,
            }
          );

          notification.onclick = () => {
            window.focus();
            navigate(`/chat/${conversationId}`);
            notification.close();
          };
        } catch (error) {
          console.error("Failed showing chat notification:", error);
        }
      }
    },
    handleSnapshotError("Chat notifications")
  );

    return () => unsub();
  }, [user, userProfile?.accountStatus, notifPrefs, navigate]);

  // 🌾 Nearby listing notifications
  useEffect(() => {
    if (
  !user ||
  userProfile?.accountStatus !== "active" ||
  !notifPrefs?.listings ||
  !userLocation ||
  !canNotify()
) {
  return;
}

    let hasInitialized = false;
    const listenerStartedAt = Date.now();

    const unsub = onSnapshot(
  collection(db, "fields"),
  (snapshot) => {
    // Ignore the first batch of already-existing docs
    if (!hasInitialized) {
      snapshot.docs.forEach((docSnap) => {
        seenListingIdsRef.current.add(docSnap.id);
      });
      hasInitialized = true;
      return;
    }

    snapshot.docChanges().forEach((change) => {
      const id = change.doc.id;

      if (change.type === "removed") {
        seenListingIdsRef.current.delete(id);
        return;
      }

      if (change.type !== "added") return;
      if (seenListingIdsRef.current.has(id)) return;

      const listing = change.doc.data();

      // Skip your own listings
      if (listing.ownerId === user.uid) {
        seenListingIdsRef.current.add(id);
        return;
      }

      let lat = listing.lat;
      let lng = listing.lng;
      let acres = listing.acres;

      if ((!lat || !lng) && Array.isArray(listing.fields) && listing.fields[0]) {
        lat = listing.fields[0].lat;
        lng = listing.fields[0].lng;
      }

      if ((!acres || Number.isNaN(Number(acres))) && Array.isArray(listing.fields)) {
        acres = listing.fields.reduce(
          (sum, field) => sum + (Number(field.acres) || 0),
          0
        );
      }

      const distance = getSafeDistanceMiles(userLocation, { lat, lng });
      if (distance === null) return;

      seenListingIdsRef.current.add(id);

      if (distance <= 50) {
        if (
          !shouldTriggerLiveAlert({
            id: `listing_${id}`,
            record: listing,
            listenerStartedAt,
            createdAt: listing.createdAt,
            updatedAt: listing.updatedAt,
          })
        ) {
          return;
        }

        try {
          playNotificationSound();

          const notification = new Notification("🌾 New listing near you", {
            body: `${Math.round(distance)} miles away • ${Number(acres || 0).toFixed(2)} acres`,
            icon: "/logo192.png",
            tag: `listing_${id}`,
          });

          notification.onclick = () => {
            window.focus();
            navigate("/map", { state: { focusJobId: id } });
            notification.close();
          };
        } catch (error) {
          console.error("Failed showing listing notification:", error);
        }
      }
    });
  },
  handleSnapshotError("Nearby listings")
);

return () => unsub();
  }, [user, userProfile?.accountStatus, notifPrefs, userLocation, navigate]);

  // 🛒 New product notifications
  useEffect(() => {
  if (
  !user ||
  userProfile?.accountStatus !== "active" ||
  !notifPrefs?.products ||
  !canNotify()
) {
  return;
}

  let hasInitialized = false;
  const listenerStartedAt = Date.now();

  const unsub = onSnapshot(collection(db, "products"), async (snapshot) => {
    // 🚫 skip initial load
    if (!hasInitialized) {
      snapshot.docs.forEach((docSnap) => {
        seenProductIdsRef.current.add(docSnap.id);
      });
      hasInitialized = true;
      return;
    }

    for (const change of snapshot.docChanges()) {
      const id = change.doc.id;

      if (change.type === "removed") {
        seenProductIdsRef.current.delete(id);
        continue;
      }

      if (change.type !== "added") continue;
      if (seenProductIdsRef.current.has(id)) continue;

      seenProductIdsRef.current.add(id);

      const product = change.doc.data();

      if (product.userId === user.uid) continue;

      let sellerName = "Someone";

      try {
        if (product.userId) {
          const sellerSnap = await getDoc(doc(db, "users", product.userId));
          if (sellerSnap.exists()) {
            sellerName = sellerSnap.data().name || sellerName;
          }
        }
      } catch (error) {}

      if (
        !shouldTriggerLiveAlert({
          id: `product_${id}`,
          record: product,
          listenerStartedAt,
          createdAt: product.createdAt,
          updatedAt: product.updatedAt,
        })
      ) {
        continue;
      }

      playNotificationSound();

      const notification = new Notification("🛒 New product listed", {
        body: `${sellerName} added ${product.name || "a new product"}`,
        icon: "/logo192.png",
        tag: `product_${id}`,
      });

      notification.onclick = () => {
        window.focus();
        navigate(`/profile/${product.userId}`);
        notification.close();
      };
    }
  },
 handleSnapshotError("Product notifications")
);

  return () => unsub();
}, [user, userProfile?.accountStatus, notifPrefs, navigate]);

    // 💰 New bid notifications for field/job owners
useEffect(() => {
  if (
    !user ||
    userProfile?.accountStatus !== "active" ||
    notifPrefs?.bids === false ||
    !canNotify()
  ) {
    return;
  }

  const bidUnsubs = [];
  const listenerStartedAt = Date.now();

  const unsubFields = onSnapshot(
    query(collection(db, "fields"), where("ownerId", "==", user.uid)),
    (fieldSnapshot) => {
      fieldSnapshot.docChanges().forEach((fieldChange) => {
        if (fieldChange.type === "removed") return;

        const fieldId = fieldChange.doc.id;
        const field = fieldChange.doc.data();

        const bidsRef = collection(db, "fields", fieldId, "bids");

        let hasInitialized = false;

        const unsubBids = onSnapshot(
          bidsRef,
          async (bidSnapshot) => {
            // 🚫 skip initial load
            if (!hasInitialized) {
              bidSnapshot.docs.forEach((docSnap) => {
                const key = `${fieldId}_${docSnap.id}`;
                seenBidIdsRef.current.add(key);
              });
              hasInitialized = true;
              return;
            }

            for (const change of bidSnapshot.docChanges()) {
              if (change.type !== "added") continue;

              const bidId = change.doc.id;
              const bid = change.doc.data();
              const uniqueKey = `${fieldId}_${bidId}`;

              if (seenBidIdsRef.current.has(uniqueKey)) continue;
              seenBidIdsRef.current.add(uniqueKey);

              if (bid.bidderId === user.uid) continue;

              let bidderName = bid.bidderName || "Someone";

              try {
                if (bid.bidderId) {
                  const bidderSnap = await getDoc(doc(db, "users", bid.bidderId));
                  if (bidderSnap.exists()) {
                    bidderName = bidderSnap.data().name || bidderName;
                  }
                }
              } catch (error) {}

              if (
                !shouldTriggerLiveAlert({
                  id: `bid_${uniqueKey}`,
                  record: bid,
                  listenerStartedAt,
                  createdAt: bid.createdAt,
                  updatedAt: bid.updatedAt,
                })
              ) {
                continue;
              }

              playNotificationSound();

              const notification = new Notification("💰 New bid received", {
                body: `${bidderName} placed a bid on ${
                  field.title || field.grassType || "your field job"
                }`,
                icon: "/logo192.png",
                tag: `bid_${uniqueKey}`,
              });

              notification.onclick = () => {
                window.focus();
                navigate("/jobs", {
                  state: {
                    dashboardView: "owner",
                    focusJobId: fieldId,
                    openOwnerDashboard: true,
                  },
                });
                notification.close();
              };
            }
          },
          handleSnapshotError("Field bids")
        );

        bidUnsubs.push(unsubBids);
      });
    },
    handleSnapshotError("Owner fields for bid notifications")
  );

  return () => {
    unsubFields();
    bidUnsubs.forEach((unsub) => unsub());
  };
}, [user, userProfile?.accountStatus, notifPrefs, navigate]);

  const shouldShowEmailVerificationCard = shouldRenderEmailVerificationOnboarding({
    user,
    isAuthPage,
    isLocalLiveParticipantPage,
    shouldSuppressOuterAppChrome,
    profileLoading,
    shouldShowWarning: () => shouldShowVerificationWarning(auth.currentUser || user, userProfile),
  });

  useEffect(() => {
    if (!shouldShowEmailVerificationCard) return;
    trackUsageEvent("email_verification_prompt_shown", {
      feature: "auth",
      action: "verification_prompt",
    });
  }, [shouldShowEmailVerificationCard]);

  if (shouldRenderNormalAppLoading({
    authLoading,
    profileLoading,
    isAdminRoute,
    isLocalLiveParticipantPage,
  })) {
  return <div className="card">Loading...</div>;
}

  const viewerLocationDiagnostics = locationDebugEnabled
    ? getLocationDiagnostics({
        id: user?.uid || "",
        type: "viewer",
        locationData: userLocation,
      })
    : null;
  const profileLocationDiagnostics = locationDebugEnabled
    ? getLocationDiagnostics({
        id: user?.uid || "",
        type: "profile",
        locationData: userProfile || {},
      })
    : null;
  const locationStorageDiagnostics =
    locationDebugEnabled && typeof window !== "undefined"
      ? {
          localStorage: Object.keys(window.localStorage || {})
            .filter((key) => /location|geo|radius/i.test(key))
            .reduce((acc, key) => ({ ...acc, [key]: window.localStorage.getItem(key) }), {}),
          sessionStorage: Object.keys(window.sessionStorage || {})
            .filter((key) => /location|geo|radius/i.test(key))
            .reduce((acc, key) => ({ ...acc, [key]: window.sessionStorage.getItem(key) }), {}),
        }
      : null;

  return (
    <>
<CampaignTrafficTracker />
<AppPageAnalyticsTracker />
          
{shouldHideNormalShellControls ? null : isAuthPage ? (
  <Navbar hideControls />
) : !user?.uid ? (
  <Navbar publicControls />
) : (
  user?.uid && <Navbar user={user} userProfile={userProfile} handleLogout={handleLogout} />
)}

{shouldRenderLocationSetupOnboarding({
  user,
  isAuthPage,
  isLocalLiveParticipantPage,
  shouldSuppressOuterAppChrome,
  showLocationPrompt,
}) && (
  <div className="location-setup-card" role="region" aria-label="Set your location">
    <div>
      <p className="eyebrow">Location setup</p>
      <h3>Where should MyFarmingPal look first?</h3>
      <p>
        Add your county/state or city/state area so nearby jobs, products,
        events, and community posts start in the right place.
      </p>
    </div>

    <form className="location-setup-form" onSubmit={handleSaveManualLocation}>
      <label className="form-label">
        Location area
        <input
          type="text"
          value={manualLocationInput}
          onChange={(event) => setManualLocationInput(event.target.value)}
          placeholder="County, state or city, state"
        />
        <p className="input-helper">
          Use a general area only. Please do not enter a home address.
        </p>
      </label>

      {locationPromptMessage && (
        <div
          className={`location-inline-message ${locationPromptMessageType}`}
          role={locationPromptMessageType === "error" ? "alert" : "status"}
        >
          {locationPromptMessage}
        </div>
      )}

      <div className="location-setup-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={handleUseCurrentLocation}
          disabled={isLocating}
        >
          {isLocating ? "Locating..." : "Use my current location"}
        </button>
        <button type="submit" className="btn-primary" disabled={isSavingLocation}>
          {isSavingLocation ? "Saving..." : "Save location"}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={handleSkipLocationSetup}
          disabled={isLocating || isSavingLocation}
        >
          Skip for now
        </button>
      </div>
    </form>
  </div>
)}

{shouldShowEmailVerificationCard && (
  <div className="email-verification-card" data-refresh={verificationRefreshKey}>
    <div>
      <p className="eyebrow">Email verification</p>
      <h3>Please verify your email</h3>
      <p>
        You can keep browsing MyFarmingPal while you wait. Posting, messaging,
        bidding, buying, reserving, and reviewing may require a verified email.
      </p>
      <p className="input-helper">
        While MyFarmingPal is in early access, the verification email may come
        from Firebase. Please check spam or promotions if you do not see it.
      </p>
    </div>

    {verificationStatusMessage && (
      <p className="auth-safe-message" role="status">
        {verificationStatusMessage}
      </p>
    )}

    <div className="email-verification-actions">
      <button
        type="button"
        className="btn-primary"
        onClick={handleResendVerificationEmail}
        disabled={verificationSending}
      >
        {verificationSending ? "Sending..." : "Resend verification email"}
      </button>

      <button
        type="button"
        className="btn-soft"
        onClick={handleRefreshVerifiedAccount}
      >
        I've verified, refresh my account
      </button>
    </div>
  </div>
)}

{isAdminRoute ? (
  <AdminRoutePage
    user={user}
    userProfile={userProfile}
    authLoading={authLoading}
    profileLoading={profileLoading}
  />
) : (
<div className="app-container">
  <AuthActionGateProvider currentUser={user}>
  <Routes>
    <Route path="/reactivate" element={<ReactivateAccount />} />

    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/community-rules" element={<CommunityRules />} />
    <Route path="/safety" element={<Safety />} />
    <Route path="/contact" element={<Contact />} />
    <Route
      path="/farm-help"
      element={
        <Suspense fallback={<RouteLoading message="Loading Farm Help Center..." />}>
          <FarmHelpCenterPage />
        </Suspense>
      }
    />
    <Route
      path="/farm/:slug"
      element={<PublicFarmProfile />}
    />
    <Route path="/commercial-demo" element={<Navigate to="/" replace />} />
    <Route path="/Admin" element={<Navigate to="/admin" replace />} />
    {isLocalhostRuntime && (
      <Route
        path="/billy-animation-test"
        element={
          <Suspense fallback={<RouteLoading message="Loading Billy test..." />}>
            <BillyAnimationTest />
          </Suspense>
        }
      />
    )}

    <Route
      path="/"
      element={
        user?.uid ? (
          <ProtectedRoute
            user={user}
            userProfile={userProfile}
            loading={authLoading || profileLoading}
          >
            <Home currentUser={user} userLocation={userLocation} userProfile={userProfile} />
          </ProtectedRoute>
        ) : (
          <PublicHomepageSwitch />
        )
      }
    />
    <Route path="/login" element={<AuthLanding setUser={setUser} />} />
    <Route path="/register" element={<AuthLanding setUser={setUser} />} />

    <Route
      path="/fall-crawl/2026"
      element={
        <Suspense fallback={<RouteLoading message="Loading The Fall Crawl 2026..." />}>
          <PublicFallCrawl2026 />
        </Suspense>
      }
    />
    <Route path="/christmas-crawl/2026/register" element={<Navigate to="/event-registration/christmas-crawl-2026" replace />} />
    <Route
      path="/event-registration/:slug"
      element={
        <Suspense fallback={<RouteLoading message="Loading event registration..." />}>
          <EventRegistrationPage />
        </Suspense>
      }
    />
    <Route
      path="/event-registration/:slug/status/:submissionId"
      element={
        <Suspense fallback={<RouteLoading message="Loading submission status..." />}>
          <EventRegistrationPage />
        </Suspense>
      }
    />

    <Route
      path="/local-live/:slug/participant/:accessCode"
      element={
        <Suspense fallback={<RouteLoading message="Opening participant workspace..." />}>
          <LocalLiveParticipant />
        </Suspense>
      }
    />
    <Route
      path="/local-live/:slug"
      element={
        <Suspense fallback={<RouteLoading message="Loading Local Live..." />}>
          <PublicLocalLive />
        </Suspense>
      }
    />

<Route
  path="/subscribe"
  element={
    <ProtectedRoute
      user={user}
      userProfile={userProfile}
      loading={authLoading || profileLoading}
    >
      <SubscribePage userProfile={userProfile} />
    </ProtectedRoute>
  }
/>

    <Route
      path="/home"
      element={
        user?.uid ? (
          <ProtectedRoute
            user={user}
            userProfile={userProfile}
            loading={authLoading || profileLoading}
          >
           <Home
             currentUser={user}
             userLocation={userLocation}
             userProfile={userProfile}
      />
          </ProtectedRoute>
        ) : (
          <PublicHome />
        )
      }
    />

    <Route
      path="/marketplace"
      element={
        <JobFeed
          currentUser={user}
          userLocation={userLocation}
          userProfile={userProfile}
          initialDashboardView="market"
        />
      }
    />

    <Route
      path="/events"
      element={
        <JobFeed
          currentUser={user}
          userLocation={userLocation}
          userProfile={userProfile}
          initialDashboardView="events"
        />
      }
    />

    <Route
      path="/farm-stands"
      element={<PublicFarmStands currentUser={user} />}
    />

    <Route
      path="/my-farm"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <MyFarm
            currentUser={user}
            userProfile={userProfile}
          />
        </ProtectedRoute>
      }
    />

    <Route
      path="/stand/:standId"
      element={
        <Suspense fallback={<RouteLoading message="Loading today's stand..." />}>
          <StandShelf currentUser={user} userProfile={userProfile} />
        </Suspense>
      }
    />

    <Route
      path="/jobs"
      element={
        <JobFeed
          currentUser={user}
          userLocation={userLocation}
          userProfile={userProfile}
          initialDashboardView={location.state?.dashboardView || "worker"}
        />
      }
    />

    <Route
      path="/plants-garden"
      element={
       <JobFeed
         currentUser={user}
         userLocation={userLocation}
         userProfile={userProfile}
         productSection={MARKETPLACE_SECTIONS.PLANTS_GARDEN}
         initialDashboardView="market"
  />
      }
    />

    <Route
      path="/almanac"
      element={
        <AlmanacPage userLocation={userLocation} userProfile={userProfile} />
      }
    />

    <Route
      path="/farm-records"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Suspense fallback={<RouteLoading message="Loading Farm Records..." />}>
            <FarmRecordsPage currentUser={user} userProfile={userProfile} />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/records"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Suspense fallback={<RouteLoading message="Loading Farm Records..." />}>
            <FarmRecordsPage currentUser={user} userProfile={userProfile} />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/animal-records"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Suspense fallback={<RouteLoading message="Loading animal records..." />}>
            <FarmRecordsPage
              currentUser={user}
              userProfile={userProfile}
              initialTab="animals"
            />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/land-share"
      element={
        <LandSharePage
          currentUser={user}
          userProfile={userProfile}
          userLocation={userLocation}
        />
      }
    />

    <Route
      path="/map"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <FieldListingForm userProfile={userProfile} userLocation={userLocation} />
        </ProtectedRoute>
      }
    />

    <Route
      path="/community"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <CommunityFeed
            currentUser={user}
            userProfile={userProfile}
            userLocation={userLocation}
          />
        </ProtectedRoute>
      }
    />

    <Route
      path="/community/alerts"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <CommunityAlertsPage
            currentUser={user}
            userProfile={userProfile}
            userLocation={userLocation}
          />
        </ProtectedRoute>
      }
    />

    <Route
      path="/inbox"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Inbox currentUser={user} userProfile={userProfile} />
        </ProtectedRoute>
      }
    />

<Route
  path="/add-product"
  element={
    <ProtectedRoute
      user={user}
      userProfile={userProfile}
      loading={authLoading || profileLoading}
      requirePremium
    >
      <AddProduct userProfile={userProfile} />
    </ProtectedRoute>
  }
/>

<Route
  path="/plants-garden/new"
  element={
    <ProtectedRoute
      user={user}
      userProfile={userProfile}
      loading={authLoading || profileLoading}
      requirePremium
    >
      <AddProduct
        userProfile={userProfile}
        defaultMarketplaceSection={MARKETPLACE_SECTIONS.PLANTS_GARDEN}
      />
    </ProtectedRoute>
  }
/>

<Route
  path="/add-farm-stand"
  element={
    <ProtectedRoute
      user={user}
      userProfile={userProfile}
      loading={authLoading || profileLoading}
      requirePremium
    >
      <AddFarmStand />
    </ProtectedRoute>
  }
/>

<Route
  path="/add-event"
  element={
    <ProtectedRoute
      user={user}
      userProfile={userProfile}
      loading={authLoading || profileLoading}
      requirePremium
    >
      <AddEvent userProfile={userProfile} />
    </ProtectedRoute>
  }
/>

    <Route
      path="/profile/:userId"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Profile userProfile={userProfile} />
        </ProtectedRoute>
      }
    />

    <Route
      path="/edit-profile"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <EditProfile setAppUserLocation={setUserLocation} />
        </ProtectedRoute>
      }
    />

    <Route
      path="/chat/:conversationId"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <ChatWrapper userProfile={userProfile} />
        </ProtectedRoute>
      }
    />

    <Route
      path="/browse-users"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <BrowseUsers />
        </ProtectedRoute>
      }
    />

    <Route
      path="/browse"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
        >
          <Navigate to="/browse-users" replace />
        </ProtectedRoute>
      }
    />

    <Route
      path="/admin"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
        >
          <Suspense fallback={<RouteLoading message="Loading admin dashboard..." />}>
            <AdminDashboard />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/local-live"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
          loginPath="/login"
        >
          <Suspense fallback={<RouteLoading message="Loading Local Live mission control..." />}>
            <AdminLocalLive />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/local-live/:eventId/homepage-preview"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
          loginPath="/login"
        >
          <Suspense fallback={<RouteLoading message="Loading Fall Crawl homepage preview..." />}>
            <FallCrawlHomepagePreview />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/local-live/:eventId/map-preview"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
          loginPath="/login"
        >
          <Suspense fallback={<RouteLoading message="Loading Local Live map preview..." />}>
            <LocalLiveMapPreview />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/local-live/:eventId/backroads-book-test"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
          loginPath="/login"
        >
          <Suspense fallback={<RouteLoading message="Loading Backroads Book test mode..." />}>
            <LocalLiveBackroadsBookTest />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/content-studio"
      element={
        <ProtectedRoute
          user={user}
          userProfile={userProfile}
          loading={authLoading || profileLoading}
          requireAdmin
          loginPath="/login"
        >
          <Suspense fallback={<RouteLoading message="Loading Content Studio..." />}>
            <ContentStudio />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
  </AuthActionGateProvider>
</div>
)}
{locationDebugEnabled && (
  <aside
    style={{
      position: "fixed",
      left: 12,
      bottom: 12,
      zIndex: 20000,
      maxWidth: 420,
      maxHeight: "45vh",
      overflow: "auto",
      padding: 12,
      borderRadius: 12,
      background: "rgba(24, 35, 26, 0.94)",
      color: "#fff",
      fontSize: 12,
      lineHeight: 1.4,
      boxShadow: "0 16px 40px rgba(0,0,0,0.28)",
    }}
  >
    <strong>Location Debug</strong>
    <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>
      {JSON.stringify(
        {
          authUid: user?.uid || null,
          activeViewerLocation: viewerLocationDiagnostics,
          profileLocation: profileLocationDiagnostics,
          rawAppUserLocation: userLocation,
          rawProfileLocation: {
            locationText: userProfile?.locationText || "",
            city: userProfile?.city || "",
            county: userProfile?.county || "",
            state: userProfile?.state || "",
            lat: userProfile?.lat ?? null,
            lng: userProfile?.lng ?? null,
            nestedLocation: userProfile?.location || null,
            locationSource: userProfile?.locationSource || "",
          },
          storage: locationStorageDiagnostics,
        },
        null,
        2
      )}
    </pre>
  </aside>
)}
{shouldShowBillyGuide && (
  <Suspense fallback={null}>
    <GoatGuide userProfile={userProfile} />
  </Suspense>
)}
    </>
  );
}

export default App;
